import React from "react";

import { DialogContent } from "@material-ui/core";
import { FormikErrors, FormikTouched, getIn, useFormikContext } from "formik";

import { Row } from "components/Layout";
import TabRow, { DefaultTab } from "components/TabSelector/TabRow";
import { TabLabel } from "components/TabSelector";

import { FormStatus } from "constants/form";
import theme from "constants/theme";

import { MobileTransactionalCreateSaleFormValues } from "../lib/types";
import { TransactionalSaleFormTabId } from "../constants";
import TabSelector from "./TabSelector";
import { getTabFields, getTabStatusColor } from "../lib/tabManager";

function getTabStatus(
  fields: string[],
  errors: FormikErrors<MobileTransactionalCreateSaleFormValues>,
  touched: FormikTouched<MobileTransactionalCreateSaleFormValues>,
): string | undefined {
  if (fields.length === 0) {
    return undefined;
  }
  const anyFieldHasError = fields.some(f => getIn(errors, f));
  const noFieldsTouched = !fields.some(f => getIn(touched, f));

  if (anyFieldHasError) {
    return FormStatus.ERROR;
  }
  if (noFieldsTouched) {
    return FormStatus.UNTOUCHED;
  }
  return FormStatus.TOUCHED;
}

interface FormProps {
  activeTab: TransactionalSaleFormTabId;
  changeTab: (id: TransactionalSaleFormTabId) => void;
  tabOrder: TransactionalSaleFormTabId[];
  saleLotNamespace: string;
  consignmentNamespace?: string;
  saleNamespace?: string;
  renderTab: (activeTab: TransactionalSaleFormTabId) => React.ReactNode;
}

export const Form = ({
  activeTab,
  changeTab,
  tabOrder,
  saleLotNamespace,
  consignmentNamespace,
  saleNamespace,
  renderTab,
}: FormProps) => {
  const { errors, touched } = useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const tabFields = getTabFields(
    saleLotNamespace,
    consignmentNamespace,
    saleNamespace,
  );

  const tabs = tabOrder.map(id => {
    const tabStatus = getTabStatus(tabFields[id], errors, touched);
    return {
      id,
      title: id,
      component: DefaultTab,
      TabSelectorComponent: TabSelector,
      TabLabelComponent: TabLabel,
      tabLabelComponentProps: { color: getTabStatusColor(tabStatus) },
    };
  });

  return (
    <>
      <Row
        fullWidth
        flex
        justifyCenter
        overflow="scroll"
        flexShrink={0}
        style={{ borderBottom: "1px" }}
      >
        <TabRow
          tabs={tabs}
          selectedTab={activeTab}
          setSelectedTab={id => changeTab(id as TransactionalSaleFormTabId)}
          backgroundColor="#fff"
          handleScrollIntoView
        />
      </Row>
      <DialogContent style={{ backgroundColor: theme.colors.grayF3 }}>
        {renderTab(activeTab)}
      </DialogContent>
    </>
  );
};
